import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AddReasonForChange,
  AdjudicationMode,
  AdjudicationParadigm,
  AdminCaseWithImagesAndReaders,
  BiopsyViews,
  Images,
  isBiopsyLevelAdjudication,
  isSingleReaderParadigm,
  NestValues,
  onlyUpdates,
  ReadersStudyStats,
  ReaderStudyStats,
  UpdateValueWithReasonForChange,
  UUID
} from "../models";
import { Resource, WriteResource } from "../types";
import {
  createCase,
  editCase,
  fetchCase,
  fetchCaseBiopsies,
  searchStudyImages,
  searchStudyReaders,
  suggestReaders
} from "../api";
import { RootState } from "../store";
import { Image } from "../models";
import { casesFetch } from "./cases";
import { castDraft } from "immer";
import { refreshCase } from "./caseImageViewer";

export enum CaseDialogMode {
  OpenForCreate = "OPEN_FOR_CREATE",
  OpenForEdit = "OPEN_FOR_EDIT",
  Closed = "CLOSED"
}

// FE-04: the adjudicator is now a deferred case-form field (assigned/removed on Save, like readers/
// images), not an immediate action. `selected = null` means "no adjudicator". `selectedBiopsyImageIds`
// carries the biopsy selection for a biopsy-level adjudicator; on edit it is seeded from the case
// view's adjudicatorImageIds so re-opening the dialog preserves the current scope.
export interface AdjudicatorForm {
  readonly selected: ReaderStudyStats | null;
  readonly selectedBiopsyImageIds: ReadonlyArray<UUID>;
}

type Form = NestValues<{
  readonly procId: string;
  readonly histoProcedureId: string | null;
  readonly subjectId: string;
  readonly visitId: string;
  readonly siteId: string | null;
  readonly images: Images;
  readonly readers: ReadersStudyStats;
  readonly adjudicator: AdjudicatorForm;
}>;

export type UpdateCaseForm = AddReasonForChange<Form>;

export type CreateCaseForm = Form;

export type CaseForm<M extends CaseDialogMode> = M extends CaseDialogMode.OpenForEdit
  ? UpdateCaseForm
  : CreateCaseForm;

function adminCaseToForm(adminCase: AdminCaseWithImagesAndReaders): UpdateCaseForm {
  return {
    procId: {
      value: adminCase.caseWithStatus.procId
    },
    histoProcedureId: {
      value: adminCase.caseWithStatus.histoProcedureId
    },
    subjectId: {
      value: adminCase.caseWithStatus.subjectId
    },
    siteId: {
      value: adminCase.caseWithStatus.siteId
    },
    visitId: {
      value: adminCase.caseWithStatus.visitId
    },
    images: {
      value: adminCase.images
    },
    readers: {
      value: adminCase.readers
    },
    adjudicator: {
      // Single-adjudicator workflow: seed from the first assigned adjudicator, if any. The biopsy
      // scope is seeded from the case view's adjudicatorImageIds so re-opening the edit dialog
      // preserves the existing selection instead of resetting it (empty for no/case-level
      // adjudicator).
      value: {
        selected: adminCase.adjudicators[0] ?? null,
        selectedBiopsyImageIds: [...adminCase.adjudicatorImageIds]
      }
    }
  };
}

export interface CaseDialogState {
  readonly mode: CaseDialogMode;
  readonly studyId: UUID | null;
  // FE-03: the study's adjudication paradigm, so the dialog can enforce the reader-count rule
  // (single CR for None, multiple otherwise) client-side, mirroring the backend (API-02 / BE-02).
  // `null` means the study hasn't loaded yet — the reader-cap check is skipped until it's known,
  // rather than assuming the most restrictive (None) paradigm and wrongly locking the picker.
  readonly adjudicationParadigm: AdjudicationParadigm | null;
  // FE-04: the study's adjudication mode, so the adjudicator section knows whether to show the
  // biopsy image picker (BIOPSY_LEVEL) or assign to the whole case (CASE_LEVEL).
  readonly adjudicationMode: AdjudicationMode;
  readonly id: UUID | null;
  readonly savedHistoCase: AdminCaseWithImagesAndReaders | null;
  readonly histoCase: WriteResource<CaseForm<CaseDialogMode>, AdminCaseWithImagesAndReaders>;
  readonly readersSearchResults: Resource<ReadersStudyStats>;
  readonly readersSearchText: string;
  readonly imagesSearchResults: Resource<Images>;
  readonly imagesSearchText: string;
  readonly readerSuggestions: Resource<ReadersStudyStats>;
  readonly refreshSingleCaseOnSuccess: boolean;
  // FE-04: adjudicator-picker SEARCH state. Kept separate from the reader search fields above so the
  // two "Assign …" boxes don't overwrite each other's text/results. The chosen adjudicator + biopsy
  // selection now live in the case form (histoCase.data.adjudicator), applied on Save.
  readonly adjudicatorSearchText: string;
  readonly adjudicatorSearchResults: Resource<ReadersStudyStats>;
  // FE-04: the case's biopsy groupings (BE-03) from the backend, for the biopsy picker.
  readonly biopsies: BiopsyViews;
}

export const initialState: CaseDialogState = {
  mode: CaseDialogMode.Closed,
  studyId: null,
  adjudicationParadigm: null,
  adjudicationMode: AdjudicationMode.CaseLevel,
  id: null,
  savedHistoCase: null,
  histoCase: {
    data: {
      procId: { value: "" },
      histoProcedureId: { value: "" },
      subjectId: { value: "" },
      siteId: { value: "" },
      visitId: { value: "" },
      images: { value: [] },
      readers: { value: [] },
      adjudicator: { value: { selected: null, selectedBiopsyImageIds: [] } }
    }
  },
  readersSearchResults: {
    resource: []
  },
  readersSearchText: "",
  imagesSearchResults: {
    resource: []
  },
  imagesSearchText: "",
  readerSuggestions: {
    resource: []
  },
  refreshSingleCaseOnSuccess: false,
  adjudicatorSearchText: "",
  adjudicatorSearchResults: {
    resource: []
  },
  biopsies: []
};

export interface OpenEditCaseDialogParams {
  readonly id: UUID;
  readonly studyId: UUID;
  // `null` while the study (and therefore its paradigm) is still loading — see CaseDialogState.
  readonly adjudicationParadigm: AdjudicationParadigm | null;
  readonly adjudicationMode: AdjudicationMode;
  readonly refreshSingleCaseOnSuccess: boolean;
}

export interface OpenCreateCaseDialogParams {
  readonly studyId: UUID | null;
  readonly adjudicationParadigm: AdjudicationParadigm | null;
  readonly adjudicationMode: AdjudicationMode;
}

// thunks
export const openEditCaseDialog = createAsyncThunk(
  "caseDialog/openEditCaseDialog",
  async (params: OpenEditCaseDialogParams, thunkApi) => {
    const { dispatch } = thunkApi;
    dispatch(setOpenEditCaseDialog(params));
    // Both fetches only need the case id and have no data dependency on each other, so fire them
    // concurrently rather than serially to save a round-trip of latency on every dialog open.
    await Promise.all([
      dispatch(fetchCaseRequest(params.id)),
      dispatch(caseDialogBiopsiesFetch(params.id))
    ]);
  }
);

export const fetchCaseRequest = createAsyncThunk(
  "caseDialog/fetchCaseRequest",
  async (caseId: UUID) => {
    const response = await fetchCase(caseId);
    return response;
  }
);

// FE-04: fetch the case's biopsy groupings (BE-03) from the backend for the adjudicator biopsy picker.
export const caseDialogBiopsiesFetch = createAsyncThunk(
  "caseDialog/biopsiesFetch",
  async (caseId: UUID) => {
    return fetchCaseBiopsies(caseId);
  }
);

export const caseSuggestReadersRequest = createAsyncThunk(
  "caseDialog/caseSuggestReadersRequest",
  async (subjectId: string, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState() as RootState;
    if (state.caseDialog.studyId !== null) {
      const response = await suggestReaders(state.caseDialog.studyId, subjectId);
      return response;
    }
  }
);

export interface CaseSearchReadersRequestParams {
  readonly name: string;
  readonly studyId: string | null;
}

export const caseSearchReadersRequest = createAsyncThunk(
  "caseDialog/caseSearchReadersRequest",
  async (params: CaseSearchReadersRequestParams, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState() as RootState;
    if (state.caseDialog.studyId) {
      const response = await searchStudyReaders(state.caseDialog.studyId, params.name);
      return response;
    }
  }
);

// FE-04: the adjudicator search uses the same study-CR search endpoint as the reader search, but
// writes to its own results field so the two "Assign …" boxes don't clobber each other's state.
export const caseSearchAdjudicatorsRequest = createAsyncThunk(
  "caseDialog/caseSearchAdjudicatorsRequest",
  async (params: CaseSearchReadersRequestParams, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState() as RootState;
    if (state.caseDialog.studyId) {
      const response = await searchStudyReaders(state.caseDialog.studyId, params.name);
      return response;
    }
  }
);

export const imagesSearch = createAsyncThunk(
  "caseDialog/imagesSearch",
  async (name: string, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const state = getState() as RootState;
    if (state.caseDialog.histoCase.data && state.caseDialog.studyId) {
      dispatch(setImageSearch(name));
      const response = await searchStudyImages(
        state.caseDialog.studyId,
        state.caseDialog.id,
        name,
        state.caseDialog.histoCase.data.images.value.map(image => image.id)
      );
      return response;
    }
  }
);

export const createCaseRequest = createAsyncThunk(
  "caseDialog/createCaseRequest",
  async (_: void, thunkApi) => {
    const { dispatch, getState } = thunkApi;

    await dispatch(createCaseRequestInner());

    const state = getState() as RootState;

    if (state.caseDialog.studyId !== null) {
      const studyId = state.caseDialog.studyId;
      if (state.caseDialog.refreshSingleCaseOnSuccess) {
        await dispatch(refreshCase());
      } else {
        await dispatch(casesFetch(studyId));
      }
    }
  }
);

export const createCaseRequestInner = createAsyncThunk(
  "caseDialog/createCaseRequestInner",
  async (_: void, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState() as RootState;
    if (
      state.caseDialog.studyId !== null &&
      state.caseDialog.histoCase.data &&
      state.caseDialog.mode === CaseDialogMode.OpenForCreate
    ) {
      const createCaseResponse = await createCase(
        state.caseDialog.studyId,
        state.caseDialog.histoCase.data.procId.value,
        state.caseDialog.histoCase.data.subjectId.value,
        state.caseDialog.histoCase.data.visitId.value,
        state.caseDialog.histoCase.data.siteId.value,
        state.caseDialog.histoCase.data.readers.value.map(readerStats => readerStats.reader.id),
        state.caseDialog.histoCase.data.images.value.map(image => image.id)
      );

      return createCaseResponse;
    }
  }
);

interface UpdatedCaseProperty {
  readonly value: string | null;
  readonly reasonForChange?: string;
}

function convertUpdatedValue(
  uv: UpdatedCaseProperty | undefined
): UpdateValueWithReasonForChange<string> | undefined {
  if (uv === undefined) {
    return undefined;
  } else {
    if ("reasonForChange" in uv) {
      return {
        value: uv.value,
        reasonForChange: uv.reasonForChange
      } as UpdateValueWithReasonForChange<string> | undefined;
    } else {
      return undefined;
    }
  }
}

export const editCaseRequest = createAsyncThunk(
  "caseDialog/editCaseRequest",
  async (_: void, thunkApi) => {
    const { dispatch, getState } = thunkApi;

    await dispatch(editCaseRequestInner());

    const state = getState() as RootState;

    if (state.caseDialog.studyId !== null) {
      if (state.caseDialog.refreshSingleCaseOnSuccess) {
        await dispatch(refreshCase());
      } else {
        if (state.caseDialog.studyId) {
          await dispatch(casesFetch(state.caseDialog.studyId));
        }
      }
    }
  }
);

const editCaseRequestInner = createAsyncThunk(
  "caseDialog/editCaseRequestInner",
  async (_: void, thunkApi) => {
    const { getState } = thunkApi;
    const state = getState() as RootState;
    const updates =
      state.caseDialog.savedHistoCase &&
      onlyUpdates(
        adminCaseToForm(state.caseDialog.savedHistoCase),
        state.caseDialog.histoCase.data
      );

    if (
      state.caseDialog.id !== null &&
      updates &&
      state.caseDialog.histoCase.data &&
      state.caseDialog.mode === CaseDialogMode.OpenForEdit
    ) {
      const editCaseResponse = await editCase(
        state.caseDialog.id,
        convertUpdatedValue(updates.procId),
        convertUpdatedValue(updates.histoProcedureId),
        convertUpdatedValue(updates.siteId),
        convertUpdatedValue(updates.subjectId),
        convertUpdatedValue(updates.visitId),
        updates.readers
          ? {
              ...updates.readers,
              value: updates.readers.value.map(
                (readerStats: ReaderStudyStats) => readerStats.reader.id
              ),
              reasonForChange:
                updates.readers &&
                updates.readers !== undefined &&
                "reasonForChange" in updates.readers
                  ? (updates.readers["reasonForChange"] as string)
                  : ""
            }
          : undefined,
        updates.images
          ? {
              ...updates.images,
              value: updates.images.value.map((image: Image) => image.id),
              reasonForChange:
                "reasonForChange" in updates.images
                  ? (updates.images["reasonForChange"] as string)
                  : ""
            }
          : undefined,
        updates.adjudicator
          ? {
              value: {
                userId: updates.adjudicator.value.selected?.reader.id ?? null,
                // Mirror the backend resolveAdjudicatorImages rule: only send imageIds when the
                // adjudicator is biopsy-level (Hybrid always; Forced when mode is biopsy-level), and
                // only when an adjudicator is actually selected. Case-level / removal send none.
                imageIds:
                  updates.adjudicator.value.selected !== null &&
                  state.caseDialog.adjudicationParadigm !== null &&
                  isBiopsyLevelAdjudication(
                    state.caseDialog.adjudicationParadigm,
                    state.caseDialog.adjudicationMode
                  )
                    ? updates.adjudicator.value.selectedBiopsyImageIds
                    : undefined
              },
              reasonForChange:
                "reasonForChange" in updates.adjudicator
                  ? (updates.adjudicator["reasonForChange"] as string)
                  : ""
            }
          : undefined
      );

      return editCaseResponse;
    }
  }
);

export const caseDialogSlice = createSlice({
  name: "caseDialog",
  initialState: initialState,
  reducers: {
    setOpenEditCaseDialog: (state, action: PayloadAction<OpenEditCaseDialogParams>) => {
      return {
        ...initialState,
        mode: CaseDialogMode.OpenForEdit,
        studyId: action.payload.studyId,
        adjudicationParadigm: action.payload.adjudicationParadigm,
        adjudicationMode: action.payload.adjudicationMode,
        id: action.payload.id,
        refreshSingleCaseOnSuccess: action.payload.refreshSingleCaseOnSuccess
      };
    },
    openCreateCaseDialog: (state, action: PayloadAction<OpenCreateCaseDialogParams>) => {
      return {
        ...initialState,
        mode: CaseDialogMode.OpenForCreate,
        studyId: action.payload.studyId,
        adjudicationParadigm: action.payload.adjudicationParadigm,
        adjudicationMode: action.payload.adjudicationMode
      };
    },
    closeCaseDialog: state => {
      state.mode = CaseDialogMode.Closed;
    },
    changeCase: (state, action: PayloadAction<CaseForm<CaseDialogMode>>) => {
      state.histoCase = {
        data: castDraft(action.payload)
      };
    },
    addReader: (state, action: PayloadAction<ReaderStudyStats>) => {
      if (state.histoCase.data) {
        const current = state.histoCase.data.readers.value;
        const alreadyAssigned = current
          .map(readerStats => readerStats.reader.id)
          .includes(action.payload.reader.id);
        // FE-03: a None-paradigm study is single-reader — reject adding a second CR (mirrors the
        // backend BE-02 rule). Non-None paradigms allow multiple. While the paradigm is unknown
        // (null, study still loading) we don't block — the backend BE-02 guard remains authoritative.
        const wouldExceedNoneLimit =
          state.adjudicationParadigm !== null &&
          isSingleReaderParadigm(state.adjudicationParadigm) &&
          current.length >= 1;
        state.histoCase = {
          data: {
            ...state.histoCase.data,
            readers: {
              value:
                !alreadyAssigned && !wouldExceedNoneLimit
                  ? castDraft([...current, action.payload])
                  : current
            }
          }
        };
      }
    },
    dropReader: (state, action: PayloadAction<ReaderStudyStats>) => {
      if (state.histoCase.data) {
        state.histoCase.data.readers = {
          value: state.histoCase.data.readers.value.filter(
            readerStudyStats => readerStudyStats.reader.id !== action.payload.reader.id
          )
        };
      }
    },
    setImageSearch: (state, action: PayloadAction<string>) => {
      state.imagesSearchText = action.payload;
      state.imagesSearchResults = { isPending: true };
    },
    assignImage: (state, action: PayloadAction<Image>) => {
      if (state.histoCase.data) {
        state.histoCase.data.images.value = [...state.histoCase.data.images.value, action.payload];
      }
    },
    unassignImage: (state, action: PayloadAction<Image>) => {
      if (state.histoCase.data) {
        state.histoCase.data.images = {
          value: state.histoCase.data.images.value.filter(
            imageWithAnnotations => imageWithAnnotations.id !== action.payload.id
          )
        };
        // Also drop the removed image from the adjudicator's biopsy selection, so a no-longer-on-case
        // image id can't be submitted on Save (the backend would reject it as "not on case").
        if (state.histoCase.data) {
          state.histoCase.data.adjudicator.value.selectedBiopsyImageIds = state.histoCase.data.adjudicator.value.selectedBiopsyImageIds.filter(
            imageId => imageId !== action.payload.id
          );
        }
      }
    },
    // FE-04: adjudicator picker. The chosen adjudicator + biopsy selection live in the case form and
    // are applied on Save (deferred), like readers/images.
    setAdjudicatorSearch: (state, action: PayloadAction<string>) => {
      state.adjudicatorSearchText = action.payload;
      state.adjudicatorSearchResults = { isPending: true };
    },
    selectAdjudicator: (state, action: PayloadAction<ReaderStudyStats>) => {
      if (state.histoCase.data) {
        state.histoCase.data.adjudicator.value.selected = castDraft(action.payload);
        // A new adjudicator starts with an empty biopsy selection (re-pick per MVP).
        state.histoCase.data.adjudicator.value.selectedBiopsyImageIds = [];
      }
    },
    clearAdjudicator: state => {
      if (state.histoCase.data) {
        state.histoCase.data.adjudicator.value = { selected: null, selectedBiopsyImageIds: [] };
      }
    },
    // Toggle a biopsy's images (BIOPSY_LEVEL): add or remove the given image ids as a set.
    toggleBiopsyImages: (state, action: PayloadAction<ReadonlyArray<UUID>>) => {
      if (state.histoCase.data) {
        const ids = action.payload;
        const current = state.histoCase.data.adjudicator.value.selectedBiopsyImageIds;
        const allSelected = ids.every(id => current.includes(id));
        state.histoCase.data.adjudicator.value.selectedBiopsyImageIds = allSelected
          ? current.filter(id => !ids.includes(id))
          : [...current, ...ids.filter(id => !current.includes(id))];
      }
    }
  },

  extraReducers: builder => {
    builder.addCase(fetchCaseRequest.pending, state => {
      state.histoCase = {
        data: state.histoCase.data,
        isPending: true
      };
    });
    builder.addCase(fetchCaseRequest.fulfilled, (state, action) => {
      if (state.mode === CaseDialogMode.OpenForEdit) {
        if (action.payload && "imageQueries" in action.payload) {
          state.savedHistoCase = castDraft(action.payload);

          state.histoCase = {
            data: castDraft(adminCaseToForm(action.payload)),
            resource: castDraft(action.payload)
          };
        }
      }
    });
    builder.addCase(fetchCaseRequest.rejected, (state, action) => {
      state.histoCase = {
        data: state.histoCase.data,
        errorMessage: action.error.message
      };
    });

    // FE-04: the case's biopsy groupings for the adjudicator biopsy picker.
    builder.addCase(caseDialogBiopsiesFetch.fulfilled, (state, action) => {
      state.biopsies = castDraft(action.payload);
    });
    builder.addCase(caseDialogBiopsiesFetch.rejected, state => {
      state.biopsies = [];
    });

    // caseSuggestReadersRequest
    builder.addCase(caseSuggestReadersRequest.fulfilled, (state, action) => {
      if (action.payload !== undefined) {
        state.readerSuggestions = { resource: castDraft(action.payload) };
      }
    });
    builder.addCase(caseSuggestReadersRequest.rejected, (state, action) => {
      state.readerSuggestions = { errorMessage: action.error.message || "" };
    });

    // caseSearchReadersRequest
    builder.addCase(caseSearchReadersRequest.pending, (state, action) => {
      state.readersSearchText = action.meta.arg.name;
      state.readersSearchResults = { isPending: true };
    });
    builder.addCase(caseSearchReadersRequest.fulfilled, (state, action) => {
      if (action.payload !== undefined) {
        state.readersSearchResults = { resource: castDraft(action.payload) };
      }
    });
    builder.addCase(caseSearchReadersRequest.rejected, (state, action) => {
      state.readersSearchResults = { errorMessage: action.error.message || "" };
    });

    // caseSearchAdjudicatorsRequest — writes to the adjudicator-only results field.
    builder.addCase(caseSearchAdjudicatorsRequest.pending, (state, action) => {
      state.adjudicatorSearchText = action.meta.arg.name;
      state.adjudicatorSearchResults = { isPending: true };
    });
    builder.addCase(caseSearchAdjudicatorsRequest.fulfilled, (state, action) => {
      if (action.payload !== undefined) {
        state.adjudicatorSearchResults = { resource: castDraft(action.payload) };
      }
    });
    builder.addCase(caseSearchAdjudicatorsRequest.rejected, (state, action) => {
      state.adjudicatorSearchResults = { errorMessage: action.error.message || "" };
    });

    // imagesSearch
    builder.addCase(imagesSearch.fulfilled, (state, action) => {
      if (action.payload !== undefined) {
        state.imagesSearchResults = { resource: castDraft(action.payload) };
      }
    });
    builder.addCase(imagesSearch.rejected, (state, action) => {
      state.imagesSearchResults = { errorMessage: action.error.message || "" };
    });

    // createCaseRequestInner
    builder.addCase(createCaseRequestInner.pending, state => {
      state.histoCase = {
        data: state.histoCase.data,
        isSaving: true
      };
    });
    builder.addCase(createCaseRequestInner.fulfilled, (state, action) => {
      if (state.studyId !== null) {
        state.mode = CaseDialogMode.Closed;
        if (action.payload !== undefined) {
          state.histoCase = {
            data: state.histoCase.data,
            resource: castDraft(action.payload)
          };
        }
      }
    });
    builder.addCase(createCaseRequestInner.rejected, (state, action) => {
      state.histoCase = {
        data: state.histoCase.data,
        errorMessage: action.error.message || ""
      };
    });

    // editCaseRequest
    builder.addCase(editCaseRequestInner.pending, state => {
      state.histoCase = {
        data: state.histoCase.data,
        isSaving: true
      };
    });
    builder.addCase(editCaseRequestInner.fulfilled, (state, action) => {
      state.mode = CaseDialogMode.Closed;
      if (action.payload !== undefined) {
        state.histoCase = {
          data: state.histoCase.data,
          resource: castDraft(action.payload as AdminCaseWithImagesAndReaders)
        };
      }
    });
    builder.addCase(editCaseRequestInner.rejected, (state, action) => {
      state.histoCase = {
        data: state.histoCase.data,
        errorMessage: action.error.message || ""
      };
    });
  }
});

export const {
  setImageSearch,
  setOpenEditCaseDialog,
  openCreateCaseDialog,
  closeCaseDialog,
  changeCase,
  addReader,
  dropReader,
  assignImage,
  unassignImage,
  setAdjudicatorSearch,
  selectAdjudicator,
  clearAdjudicator,
  toggleBiopsyImages
} = caseDialogSlice.actions;

export default caseDialogSlice.reducer;
